// src/admin/utils/adminFormatters.js
//
// Presentation-only formatting helpers for the Admin / Founder Dashboard.
// These functions NEVER calculate financial values — they only format
// numbers that were already computed by the backend.

const INR_FORMATTER = new Intl.NumberFormat('en-IN', {
  style: 'currency',
  currency: 'INR',
  maximumFractionDigits: 0,
});

const NUMBER_FORMATTER = new Intl.NumberFormat('en-IN');

function isEmpty(value) {
  return value === null || value === undefined || value === '';
}

/**
 * Format a value as Indian Rupees, e.g. formatCurrency(125000) => "₹1,25,000"
 * Returns "Not available" for null/undefined/"" so we never fake a ₹0.
 */
export function formatCurrency(value) {
  if (isEmpty(value)) return 'Not available';

  const num = Number(value);
  if (!Number.isFinite(num)) return 'Not available';

  const formatted = INR_FORMATTER.format(Math.abs(num));
  return num < 0 ? `-${formatted}` : formatted;
}

/**
 * Format a value as a percentage, e.g. formatPercentage(30) => "30.00%"
 */
export function formatPercentage(value) {
  if (isEmpty(value)) return 'Not available';

  const num = Number(value);
  if (!Number.isFinite(num)) return 'Not available';

  return `${num.toFixed(2)}%`;
}

/**
 * Format an ISO/date-like value as "20 Aug 2026"
 */
export function formatDate(value) {
  if (isEmpty(value)) return 'Not available';

  const date = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(date.getTime())) return 'Not available';

  return date.toLocaleDateString('en-IN', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  });
}

/**
 * Format a date range, e.g. "20 Aug - 22 Aug 2026"
 */
export function formatDateRange(startDate, endDate) {
  if (isEmpty(startDate) && isEmpty(endDate)) return 'Not available';
  if (isEmpty(endDate)) return formatDate(startDate);
  if (isEmpty(startDate)) return formatDate(endDate);

  const start = new Date(startDate);
  const end = new Date(endDate);

  if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) {
    return 'Not available';
  }

  const startLabel = start.toLocaleDateString('en-IN', { day: '2-digit', month: 'short' });
  const sameYear = start.getFullYear() === end.getFullYear();
  const endLabel = end.toLocaleDateString('en-IN', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  });

  return sameYear ? `${startLabel} - ${endLabel}` : `${formatDate(start)} - ${endLabel}`;
}

/**
 * Format a plain number with Indian digit grouping, e.g. formatNumber(12345) => "12,345"
 */
export function formatNumber(value) {
  if (isEmpty(value)) return '0';

  const num = Number(value);
  if (!Number.isFinite(num)) return '0';

  return NUMBER_FORMATTER.format(num);
}

/**
 * Convert YYYY-MM-DD <-> Date safely for the date selector.
 */
export function formatDateYMD(date) {
  const d = date instanceof Date ? date : new Date(date);
  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

export function parseYMD(value) {
  const [year, month, day] = value.split('-').map(Number);
  return new Date(year, month - 1, day);
}

export function isSameDay(a, b) {
  return (
    a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth() &&
    a.getDate() === b.getDate()
  );
}

export function addDays(date, amount) {
  const d = new Date(date);
  d.setDate(d.getDate() + amount);
  return d;
}
